import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../stateManagement/store';
import {
    fetchServiceProviders,
    selectServiceProviders,
    selectServiceProvidersLoading,
    createServiceProviderAsync,
    deleteServiceProviderAsync
} from '../../stateManagement/serviceProviderSlice';
import { removeSelectedOrganization, selectSelectedOrganization } from '../../stateManagement/selectedOrganizationSlice';
import { removeSelectedServiceProvider, setSelectedServiceProvider } from '../../stateManagement/selectedServiceProviderSlice';
import GridTable, { GridColumnType } from '../../gridTable/GridTable';
import { IServiceProvider, IServiceProviderRequest } from "@ontime/types";
import ServiceProviderDialog, { serviceProviderSchema } from './ServiceProviderDialog';
import { useSnackbar } from 'notistack';
import { z } from 'zod';


const ServiceProviderList: React.FC = () => {

    const organization = useSelector(selectSelectedOrganization); // ✅ Get orgId from state
    const serviceProviders = useSelector(selectServiceProviders);
    const loading = useSelector(selectServiceProvidersLoading);

    const serviceProviderType: GridColumnType<IServiceProvider> = {
        serviceProviderId: { type: "Id", dontShow: true },
        organizationId: { type: "Integer", dontShow: true },
        name: { type: "Text", header: 'Name', showInCreateRow: true },
        phone: { type: "Phone", header: 'Phone', showInCreateRow: true },
        email: { type: "Text", header: 'Email', showInCreateRow: true },
        isPublic: { type: 'Text', header: 'Is public' },
    };

    const dispatch = useDispatch<AppDispatch>();

    const [showDeleted, setShowDeleted] = useState<boolean>(false);
    const onClickDeleteFolder = () => {
        setShowDeleted(!showDeleted);
    }

    // Fetch providers when org changes
    useEffect(() => {
        if (organization && organization.organizationId) {
            dispatch(fetchServiceProviders({ organizationId: organization.organizationId, showDeleted }));
        }
    }, [dispatch, organization, showDeleted]);


    const handleBackToOrganizations = () => {
        dispatch(removeSelectedOrganization());
        dispatch(removeSelectedServiceProvider());
    };

    const handleSelect = (input: IServiceProvider) => {
        dispatch(setSelectedServiceProvider(input))
    }

    const [serviceProviderState, setServiceProviderState] = useState<{ open: boolean; serviceProviderInput?: IServiceProvider }>({ open: false });


    const handleFormOpen = () => {
        setServiceProviderState({ open: true });
    };

    const handleEdit = async (input: IServiceProvider) => {
        setServiceProviderState({ open: true, serviceProviderInput: input });
    };

    const closeServiceProviderDialog = (input?: IServiceProvider) => {
        setServiceProviderState({ open: false, serviceProviderInput: undefined })
    }

    const [rowLoading, setRowLoading] = useState<string[]>([]);

    const handleDelete = async (input: IServiceProvider) => {
        if (input.organizationId) {
            setRowLoading([...rowLoading, input.serviceProviderId]);
            dispatch(deleteServiceProviderAsync({
                organizationId: input?.organizationId ?? "",
                serviceProviderId: input.serviceProviderId,
                currentIsDeleted: input.isDeleted ?? false,
            }))
                .unwrap()
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setRowLoading(rowLoading.filter((item) => item !== input.serviceProviderId))
                });
        }
    };

    const { enqueueSnackbar } = useSnackbar();

    const [createRowDto, setCreateRowDto] = useState<IServiceProviderRequest>({ serviceProviderId: '', name: '', isPublic: true });
    const [createRowLoading, setCreateRowLoading] = useState(false);

    const handleSave = async (data: IServiceProviderRequest) => {

        try {
            const validated = serviceProviderSchema.parse(data);
            setCreateRowLoading(true);

            dispatch(createServiceProviderAsync({ organizationId: organization?.organizationId ?? '', data }))
                .unwrap()
                .catch((error) => {
                    console.error(error, { variant: 'error' });
                })
                .finally(() => {
                    setCreateRowDto({ serviceProviderId: '', name: '', isPublic: true });
                    setCreateRowLoading(false);
                });

        } catch (error) {
            if (error instanceof z.ZodError) {
                error.issues.map(item => {
                    enqueueSnackbar(item.message, { variant: 'error' })
                }
                );
            }
        }
    };

    return (
        <>
            <GridTable
                gridTitle='Service Provider list'
                gridTitleInfo='In below list select service provider to manage its availabilities, appointments, and ...'
                loading={loading || createRowLoading}
                list={serviceProviders.map((item) => ({ ...item, id: item.serviceProviderId }))}
                inputType={serviceProviderType}
                onClickRow={handleSelect}
                onClickBack={handleBackToOrganizations}
                onClickBackInfo='Back to place list'
                onClickNew={handleFormOpen}
                onEditRow={handleEdit}
                onDeleteRow={handleDelete}
                rowLoading={rowLoading}
                createRowDto={createRowDto}
                setCreateRowDto={setCreateRowDto}
                handleCreateRow={handleSave}
                onClickDeleteFolder={onClickDeleteFolder}
                showDeleted={showDeleted}
            />
            <ServiceProviderDialog state={serviceProviderState} closeServiceProviderDialog={closeServiceProviderDialog} />
        </>
    );
};

export default ServiceProviderList;